import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import './Admin.css';

export default function AdminResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!token) {
      setError('Invalid reset link. Request a new one from forgot password.');
      return;
    }
    setLoading(true);
    try {
      await api('/api/admin/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box className="admin-login admin-login-vuexy">
        <Box className="admin-login-left" />
        <Box className="admin-login-right">
          <Typography variant="h5" className="admin-login-logo" sx={{ fontWeight: 600 }}>
            Gem Fashion Admin
          </Typography>
          <Box className="admin-login-form-wrap">
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Password reset
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Your password has been reset. You can now sign in.
            </Typography>
            <Button component={Link} to="/admin/login" variant="contained" sx={{ textTransform: 'none' }}>
              Sign in
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="admin-login admin-login-vuexy">
      <Box className="admin-login-left" />
      <Box className="admin-login-right">
        <Typography variant="h5" className="admin-login-logo" sx={{ fontWeight: 600 }}>
          Gem Fashion Admin
        </Typography>
        <Box className="admin-login-form-wrap">
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>
            Set new password
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Enter your new password below.
          </Typography>
          <Box component="form" onSubmit={handleSubmit} noValidate className="admin-login-form">
            {error && (
              <Typography color="error" sx={{ mb: 2, fontSize: '0.875rem' }}>
                {error}
              </Typography>
            )}
            <TextField
              label="New password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              required
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              sx={{ mb: 2 }}
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password"
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                    >
                      {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 2 }}
              variant="outlined"
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !token}
              sx={{ mt: 1, py: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              {loading ? 'Resetting…' : 'Reset password'}
            </Button>
            <Button component={Link} to="/admin/login" fullWidth sx={{ mt: 2, textTransform: 'none' }}>
              Back to login
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
